import TrainingRateDialog from '@/components/TrainingRateDialog'
import VideoPlayer from '@/components/VideoPlayer'
import { COURSE_STATUS } from '@/libs/core/constants'
import durationToStr from '@/libs/utils/durationToStr'
import SpinnerDialog from '@/shared/SpinnerDialog'
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import round from 'lodash/round'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

const { COMPLETED, IN_PROGRESS, NOT_STARTED } = COURSE_STATUS

const mapStateToProps = ({ session, loading }) => ({
  summary: session.summary,
  courseList: session.courseList,
  course: session.selectedCourse,
  watchHistory: session.watchHistory,
  isLoading: loading['session/getCourseDetails'],
  rateLoading: loading['session/rateCourse'],
})

const TrainingCourse = ({
  dispatch,
  course,
  isLoading,
  watchHistory,
  rateLoading,
  summary,
  courseList = [],
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const playerRef = useRef()
  const { sessionToken, courseRecordDetailId } = useParams()
  const [maxPlayed, setMaxPlayed] = useState(0)

  const { pendingCount } = summary || {}

  const {
    courseRecordDetailId: currentCourseId,
    courseName,
    courseDesc,
    courseLength,
    courseSrc,
    courseRecordDetailStatus,
    videoWatchTime,
    goodRatingCnt,
  } = course || {}

  const [showRate, setShowRate] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)

  const cleanupRef = useRef()

  const watchTime = get(watchHistory, `${sessionToken}.${courseRecordDetailId}`, 0)

  useEffect(() => {
    if (sessionToken && courseRecordDetailId) {
      if (!summary) {
        dispatch({ type: 'session/getSessionSummary', payload: { sessionToken } })
      }

      dispatch({
        type: 'session/getCourseDetails',
        payload: { sessionToken, courseRecordDetailId },
        successCallback: data => {
          if (get(data, 'courseRecordDetailStatus', '') === COMPLETED) {
            setShowRate(true)
            playerRef.current?.shouldRestart(true)
          } else {
            playerRef.current?.shouldRestart(false)
          }

          dispatch({
            type: 'session/syncWatchHistory',
            payload: { sessionToken, courseRecordDetailId },
          })
        },
      })
    }
  }, [dispatch, sessionToken, courseRecordDetailId, summary])

  const updateCourseWatch = useCallback(
    params => {
      if (
        currentCourseId?.toString() !== courseRecordDetailId?.toString() ||
        courseRecordDetailStatus === COMPLETED
      ) {
        setShowSpinner(false)
        return
      }

      const { status } = params || {}
      const newWatchTime = Math.min(
        videoWatchTime > maxPlayed ? videoWatchTime : round(maxPlayed),
        courseLength,
      )
      const finalStatus = status || (newWatchTime >= courseLength ? COMPLETED : IN_PROGRESS)

      if (newWatchTime >= 0) {
        dispatch({
          type: 'session/updateCourseWatch',
          payload: {
            sessionToken,
            courseRecordDetailId,
            data: {
              courseRecordDetailStatus: finalStatus,
              videoWatchTime: status === COMPLETED ? courseLength : newWatchTime,
            },
          },
          successCallback: () => {
            if (finalStatus === COMPLETED) {
              dispatch({
                type: 'session/getSessionSummary',
                payload: { sessionToken },
                successCallback: () => {
                  setShowSpinner(false)
                  setShowRate(true)
                },
              })
            }

            dispatch({
              type: 'session/removeWatchHistory',
              payload: { sessionToken, courseRecordDetailId },
            })
          },
        })
      }
    },
    [
      currentCourseId,
      courseLength,
      courseRecordDetailId,
      courseRecordDetailStatus,
      dispatch,
      maxPlayed,
      sessionToken,
      videoWatchTime,
    ],
  )

  const rateCourse = (rating, callback) => {
    dispatch({
      type: 'session/rateCourse',
      payload: {
        sessionToken,
        courseRecordDetailId,
        data: {
          rating,
        },
      },
      successCallback: () => {
        setShowRate(false)
        callback?.()
      },
    })
  }

  const goBack = () => {
    navigate(`/training_session/${sessionToken}`)
  }

  useEffect(() => {
    setMaxPlayed(videoWatchTime)
  }, [videoWatchTime])

  useEffect(() => {
    cleanupRef.current = updateCourseWatch
  }, [updateCourseWatch])

  useEffect(() => {
    dispatch({ type: 'session/setCourse' })
  }, [dispatch])

  useEffect(
    () => () => {
      cleanupRef.current?.()
    },
    [],
  )

  return (
    <Container>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ flex: 1 }} />
        <Button variant="text" onClick={goBack} sx={{ mb: 1 }}>
          {t('general.back')}
        </Button>
      </Box>
      {course && (
        <Card>
          <CardContent sx={{ color: theme => theme.palette.other.grey[60].main }}>
            <Grid item container spacing={1}>
              <Grid item xs={12}>
                <Box display="flex" color="black">
                  <Typography>{courseName}</Typography>
                  <Typography sx={{ ml: 3 }}>{`${t('general.course_duration')} ${durationToStr(
                    courseLength,
                  )}`}</Typography>
                  <Typography color="black" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    {t('general.good_rating_count')}
                    <ThumbUpAlt color="primary" sx={{ mx: 1 }} />
                    {goodRatingCnt || 0}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography>{`${t('general.course_desc')}: ${courseDesc}`}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography>{t('course.notice')}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Typography color="black">{`${t('general.course_status')}: ${t(
                    `status.${courseRecordDetailStatus}`,
                  )}`}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
              <VideoPlayer
                ref={playerRef}
                maxHeight="calc(100vh - 87px)"
                videoUrl={courseSrc}
                defaultWatchTime={videoWatchTime}
                onPlay={() => {
                  updateCourseWatch()
                }}
                onVideoEnd={() => {
                  setMaxPlayed(courseLength)
                  setShowSpinner(true)
                  updateCourseWatch({ status: COMPLETED })
                }}
                onProgress={p => {
                  if (
                    playerRef.current?.playing &&
                    p.playedSeconds > maxPlayed &&
                    p.playedSeconds > watchTime
                  ) {
                    setMaxPlayed(p.playedSeconds)

                    dispatch({
                      type: 'session/updateWatchHistory',
                      payload: { sessionToken, courseRecordDetailId, watchTime: p.playedSeconds },
                    })
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
      <TrainingRateDialog
        courseCount={pendingCount}
        open={showRate}
        onRateSubmit={rateCourse}
        onFinish={() => {}}
        onNext={
          pendingCount > 0
            ? () => {
                const { courseRecordDetailId: nextId } =
                  courseList.find(c =>
                    [IN_PROGRESS, NOT_STARTED].includes(c.courseRecordDetailStatus),
                  ) || {}

                if (nextId) {
                  navigate(`/training_session/${sessionToken}/course/${nextId}`)
                }
              }
            : null
        }
        isLoading={rateLoading}
      />

      <SpinnerDialog open={!showRate && (isLoading || showSpinner)} />
    </Container>
  )
}

export default connect(mapStateToProps)(TrainingCourse)
